import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { baseAPI } from '~shared/api/base-api';

import { FALLBACK_IMPORT_HEADER_ALIASES } from '~features/import-wizard/header-aliases-fallback';

import type {
  CreateImportJobConfig,
  ImportHeaderAliasesResponse,
  ImportJobResponse,
  ImportJobStatus,
  ImportJobSummary,
} from './types';

export const getImportHeaderAliases = async () => {
  const { data } = await baseAPI.get<ImportHeaderAliasesResponse>(
    '/import-jobs/header-aliases',
  );
  return data;
};

export const createImportJob = async (
  config: CreateImportJobConfig,
  files?: { classCsv?: File; parentCsv?: File },
) => {
  const formData = new FormData();
  formData.append('config', JSON.stringify(config));
  if (files?.classCsv) formData.append('classCsv', files.classCsv);
  if (files?.parentCsv) formData.append('parentCsv', files.parentCsv);
  const { data } = await baseAPI.post<{
    jobId: string;
    wizardSessionId: string;
    status: string;
  }>('/import-jobs', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

export const updateImportJobDraft = async (
  jobId: string,
  config: CreateImportJobConfig,
  files?: { classCsv?: File; parentCsv?: File },
) => {
  const formData = new FormData();
  formData.append('config', JSON.stringify(config));
  if (files?.classCsv) formData.append('classCsv', files.classCsv);
  if (files?.parentCsv) formData.append('parentCsv', files.parentCsv);
  const { data } = await baseAPI.patch<ImportJobResponse>(
    `/import-jobs/${jobId}/draft`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
};

export const stageImportJobImages = async (
  jobId: string,
  images: Array<{ file: File; rowIndex: number; classTitle: string }>,
) => {
  const formData = new FormData();
  const metadata = images.map(({ rowIndex, classTitle }) => ({
    rowIndex,
    classTitle,
  }));
  formData.append('metadata', JSON.stringify(metadata));
  for (const img of images) {
    formData.append('images', img.file);
  }
  const { data } = await baseAPI.post<{ stagedCount: number }>(
    `/import-jobs/${jobId}/images`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
};

export const launchImportJob = async (jobId: string) => {
  const { data } = await baseAPI.post<{
    jobId: string;
    status: string;
    message: string;
  }>(`/import-jobs/${jobId}/launch`);
  return data;
};

export const getImportJobStatus = async (jobId: string) => {
  const { data } = await baseAPI.get<ImportJobResponse>(
    `/import-jobs/${jobId}`,
  );
  return data;
};

export const listImportJobs = async (params?: {
  status?: ImportJobStatus;
  openOnly?: boolean;
  limit?: number;
}) => {
  const search = new URLSearchParams();
  if (params?.status) search.set('status', params.status);
  if (params?.openOnly === false) search.set('openOnly', 'false');
  if (params?.limit != null) search.set('limit', String(params.limit));
  const qs = search.toString();
  const { data } = await baseAPI.get<ImportJobSummary[]>(
    `/import-jobs${qs ? `?${qs}` : ''}`,
  );
  return data;
};

export const downloadImportDraftCsv = async (
  jobId: string,
  dataset: 'classes' | 'parents',
) => {
  const { data } = await baseAPI.get<string>(
    `/import-jobs/${jobId}/csv/${dataset}`,
    { responseType: 'text' },
  );
  return data;
};

export const closeImportSession = async (jobId: string) => {
  const { data } = await baseAPI.post<{
    success: boolean;
    jobId: string;
    sessionClosedAt: string;
  }>(`/import-jobs/${jobId}/close`);
  return data;
};

export const discardImportDraft = async (jobId: string) => {
  const { data } = await baseAPI.post<{
    success: boolean;
    jobId: string;
    status: string;
    sessionClosedAt: string;
  }>(`/import-jobs/${jobId}/discard`);
  return data;
};

export const cancelImportJob = async (jobId: string) => {
  const { data } = await baseAPI.post<{ success: boolean; status: string }>(
    `/import-jobs/${jobId}/cancel`,
  );
  return data;
};

export const downloadImportErrorsCsv = async (
  jobId: string,
  dataset: 'classes' | 'parents',
) => {
  const { data } = await baseAPI.get<Blob>(
    `/import-jobs/${jobId}/errors/${dataset}`,
    { responseType: 'blob' },
  );
  return data;
};

export const sendWelcomeEmailTest = async (payload: {
  parentEmail: string;
  parentFirstName?: string;
  parentLastName?: string;
}) => {
  const { data } = await baseAPI.post<{ success: boolean }>(
    '/parents/welcome-email/test',
    payload,
  );
  return data;
};

export const useImportHeaderAliases = () =>
  useQuery({
    queryKey: ['import-job-header-aliases'],
    queryFn: getImportHeaderAliases,
    staleTime: Infinity,
    placeholderData: FALLBACK_IMPORT_HEADER_ALIASES,
  });

export const useImportJobStatus = (jobId: string, enabled: boolean) =>
  useQuery({
    queryKey: ['import-job', jobId],
    queryFn: () => getImportJobStatus(jobId),
    enabled: enabled && !!jobId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'processing' || status === 'queued') return 2000;
      return false;
    },
  });

export const useOpenImportJobs = (enabled = true) =>
  useQuery({
    queryKey: ['import-jobs', 'open'],
    queryFn: () => listImportJobs({ openOnly: true }),
    enabled,
    staleTime: 30_000,
  });

export const useImportJobAuditList = (params?: {
  status?: ImportJobStatus;
  enabled?: boolean;
}) =>
  useQuery({
    queryKey: ['import-jobs', 'audit', params?.status ?? 'all'],
    queryFn: () =>
      listImportJobs({
        openOnly: false,
        limit: 50,
        status: params?.status,
      }),
    enabled: params?.enabled !== false,
    staleTime: 30_000,
  });

export const useImportJobDetail = (jobId: string, enabled: boolean) =>
  useQuery({
    queryKey: ['import-job', jobId],
    queryFn: () => getImportJobStatus(jobId),
    enabled: enabled && !!jobId,
  });

export const useCreateImportJobMutation = () =>
  useMutation({
    mutationFn: ({
      config,
      files,
    }: {
      config: CreateImportJobConfig;
      files?: { classCsv?: File; parentCsv?: File };
    }) => createImportJob(config, files),
  });

export const useUpdateImportJobDraftMutation = () =>
  useMutation({
    mutationFn: ({
      jobId,
      config,
      files,
    }: {
      jobId: string;
      config: CreateImportJobConfig;
      files?: { classCsv?: File; parentCsv?: File };
    }) => updateImportJobDraft(jobId, config, files),
  });

export const useLaunchImportJobMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: launchImportJob,
    onSuccess: (_data, jobId) => {
      void queryClient.invalidateQueries({ queryKey: ['import-job', jobId] });
    },
  });
};

export const useStageImportImagesMutation = () =>
  useMutation({
    mutationFn: ({
      jobId,
      images,
    }: {
      jobId: string;
      images: Array<{ file: File; rowIndex: number; classTitle: string }>;
    }) => stageImportJobImages(jobId, images),
  });

export const useCloseImportSessionMutation = () =>
  useMutation({ mutationFn: closeImportSession });

export const useDiscardImportDraftMutation = () =>
  useMutation({ mutationFn: discardImportDraft });

export const useCancelImportJobMutation = () =>
  useMutation({ mutationFn: cancelImportJob });
